// Shared rubric parsing utilities.
// Extracted from FlowRubric.js so CreateAssignmentModal can reuse the same
// AI-text -> structured-criteria parsing without a circular import
// (FlowRubric imports CCSS_STANDARDS from CreateAssignmentModal).

export const LEVEL_LABELS = ['Exceeding', 'Meeting', 'Approaching', 'Beginning'];
export const LEVEL_SCORES = [4, 3, 2, 1];

// The model is asked for plain text and still returns markdown: bold markers
// around labels and phrases, and list bullets before each descriptor. Strip
// them, or the teacher's editable rubric arrives full of literal asterisks.
const cleanText = (value) => (value || '')
  .replace(/^[*\-+]\s+/, '')
  .replace(/\*\*/g, '')
  .trim();

/**
 * Parse AI-generated rubric text into structured criteria array.
 * Looks for patterns like "4 - Exceeding", "**4**", "4:", etc.
 * Falls back to a single criterion with full text if parsing fails.
 * Output shape: [{ criterion, levels: [{ score, label, description }] }]
 */
export const parseRubricText = (text) => {
  if (!text || typeof text !== 'string') return [];

  // Try to split into criteria sections first
  // Look for criterion headers like "Criterion:", "**Criterion Name**", or numbered items
  const criterionBlocks = [];

  // Strategy 1: Split by criterion headers (lines that don't start with a score number)
  const lines = text.split('\n').filter(l => l.trim());

  let currentCriterion = null;
  let currentLevels = {};
  // The level whose description we are currently inside. Tracked explicitly:
  // deriving it with Math.max over the collected scores attributes every
  // level's text to score 4, because the model emits 4, 3, 2, 1 in that order.
  let currentScore = null;

  for (const line of lines) {
    const trimmed = line.trim();

    // Check if this line starts a new score level: "4 - Exceeding", "4:", "**4**", "4 –"
    const scoreMatch = trimmed.match(/^\*{0,2}(\d)\*{0,2}\s*[-–:\.]\s*\*{0,2}(Exceeding|Meeting|Approaching|Beginning)\*{0,2}\s*[-–:\.]*\s*(.*)/i);

    if (scoreMatch) {
      const score = parseInt(scoreMatch[1]);
      const desc = cleanText(scoreMatch[3]);
      if (score >= 1 && score <= 4) {
        currentLevels[score] = desc;
        currentScore = score;
      }
      continue;
    }

    // Check if line is a continuation of the previous level description
    const continuationMatch = trimmed.match(/^\*{0,2}(\d)\*{0,2}\s*[-–:\.]\s*(.*)/);
    if (continuationMatch) {
      const score = parseInt(continuationMatch[1]);
      const desc = cleanText(continuationMatch[2]);
      if (score >= 1 && score <= 4) {
        currentLevels[score] = desc;
        currentScore = score;
      }
      continue;
    }

    // If line doesn't match a score pattern, it might be a criterion header
    // Detect headers: "**Name**", "### Name", "Name:", or lines without score numbers
    const headerMatch = trimmed.match(/^(?:\*{2}|#{1,3}\s*)?([A-Za-z][A-Za-z\s&/,]+?)(?:\*{2}|:)?\s*$/);
    if (headerMatch && !trimmed.match(/^\d/) && trimmed.length < 80) {
      // Save previous criterion if we have levels
      if (currentCriterion && Object.keys(currentLevels).length > 0) {
        criterionBlocks.push({ name: currentCriterion, levels: { ...currentLevels } });
      }
      currentCriterion = headerMatch[1].trim();
      currentLevels = {};
      currentScore = null;
      continue;
    }

    // Description text for the level we are currently inside. The model
    // routinely puts the label on its own line ("**4 - Exceeding**") and the
    // descriptors on the lines below it, so append even when nothing has been
    // collected for this level yet. Guarding on a truthy accumulator here is
    // what silently dropped every descriptor and left the rubric blank.
    if (currentScore !== null) {
      const extra = cleanText(trimmed);
      if (extra) {
        currentLevels[currentScore] = currentLevels[currentScore]
          ? `${currentLevels[currentScore]} ${extra}`
          : extra;
      }
    }
  }

  // Push final criterion
  if (currentCriterion && Object.keys(currentLevels).length > 0) {
    criterionBlocks.push({ name: currentCriterion, levels: { ...currentLevels } });
  }

  // If we found structured criteria, convert them
  if (criterionBlocks.length > 0) {
    return criterionBlocks.map(block => ({
      criterion: block.name,
      levels: LEVEL_SCORES.map((score, i) => ({
        score,
        label: LEVEL_LABELS[i],
        description: block.levels[score] || '',
      })),
    }));
  }

  // No criterion headers were found. The generation prompt asks for a single
  // 4-level rubric with no criterion headings, so this is the ordinary path,
  // not an error path. Emit one criterion from the levels the loop already
  // accumulated. Re-scanning the text here instead is what discarded every
  // description line and produced an empty rubric under a success message.
  if (Object.keys(currentLevels).length > 0) {
    return [{
      criterion: 'General',
      levels: LEVEL_SCORES.map((score, i) => ({
        score,
        label: LEVEL_LABELS[i],
        description: currentLevels[score] || '',
      })),
    }];
  }

  // Last resort: single criterion with full text
  return [{
    criterion: 'General',
    levels: LEVEL_SCORES.map((score, i) => ({
      score,
      label: LEVEL_LABELS[i],
      description: i === 0 ? text : '',
    })),
  }];
};
